import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AgentLog, AgentReplayResult } from "@/types/agent-types";
import { agentApiFixed as agentApi } from "@/lib/api/agent-api-fixed";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/use-toast";
import { Loader2, Play, RefreshCw, RotateCcw } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { format } from "date-fns";

interface ReplayLogProps {
  log: AgentLog;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ReplayLog = ({ log, open, onOpenChange }: ReplayLogProps) => {
  // A different log must not inherit another agent's custom prompt or result.
  return (
    <ReplayLogContent
      key={JSON.stringify([log.agent_type, log._id || log.traceId])}
      log={log}
      open={open}
      onOpenChange={onOpenChange}
    />
  );
};

const ReplayLogContent = ({ log, open, onOpenChange }: ReplayLogProps) => {
  const [useCustomPrompt, setUseCustomPrompt] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const [replayResult, setReplayResult] = useState<AgentReplayResult | null>(null);
  const [activeTab, setActiveTab] = useState("original");

  // Get the latest prompt for the agent type
  const {
    data: latestPrompt,
    isFetching: isFetchingPrompt,
    isError: isPromptError,
    error: promptError,
    refetch: refetchLatestPrompt,
  } = useQuery({
    queryKey: ["latestPrompt", log.agent_type],
    queryFn: () => agentApi.getLatestPrompt(log.agent_type),
    enabled: open && !useCustomPrompt,
    retry: false,
  });

  const hasCurrentPrompt = !!latestPrompt && !isFetchingPrompt && !isPromptError;
  const canReplay = useCustomPrompt ? !!customPrompt.trim() : hasCurrentPrompt;

  // Mutation for replaying a log
  const replayMutation = useMutation({
    mutationFn: async () => {
      const replayedLog = await agentApi.replayLog(log._id || log.traceId, {
        useCurrentPrompt: !useCustomPrompt,
        customPrompt: useCustomPrompt ? customPrompt : undefined,
      });
      
      return {
        originalLog: log,
        newLog: replayedLog
      };
    },
    onSuccess: (data) => {
      setReplayResult(data);
      toast({
        title: "Success",
        description: "Log replayed successfully",
        variant: "default",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to replay log: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  const handleReplay = () => {
    if (!canReplay) return;
    replayMutation.mutate();
  };

  const handleReset = () => {
    setReplayResult(null);
    setActiveTab("original");
  };

  // Function to pretty print JSON
  const prettyPrintJSON = (json: any) => {
    try {
      if (typeof json === 'string') {
        json = JSON.parse(json);
      }
      return JSON.stringify(json, null, 2);
    } catch (e) {
      return typeof json === 'string' ? json : JSON.stringify(json);
    }
  };

  const formatTimestamp = (timestamp: string | number | Date) => {
    try {
      return format(new Date(timestamp), "MMM d, yyyy h:mm:ss a");
    } catch (e) {
      return "Invalid date";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Replay Agent Log</DialogTitle>
          <DialogDescription>
            Replay this log with the current prompt or a custom prompt
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col space-y-4 overflow-hidden">
          {/* Input Preview */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Agent Input</h3>
            <div className="bg-muted rounded-md p-3 overflow-auto max-h-[150px]">
              <pre className="text-xs font-mono whitespace-pre-wrap">
                {prettyPrintJSON(log.input)}
              </pre>
            </div>
          </div>

          {/* Prompt Selection */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium">Agent Prompt</h3>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setUseCustomPrompt(false)}
                  className={!useCustomPrompt ? "bg-primary text-primary-foreground" : ""}
                >
                  Current
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (!customPrompt && hasCurrentPrompt) setCustomPrompt(latestPrompt.prompt);
                    setUseCustomPrompt(true);
                  }}
                  className={useCustomPrompt ? "bg-primary text-primary-foreground" : ""}
                >
                  Custom
                </Button>
              </div>
            </div>

            <Textarea
              aria-label="Agent prompt"
              value={useCustomPrompt ? customPrompt : (hasCurrentPrompt ? latestPrompt.prompt : "")}
              onChange={(e) => setCustomPrompt(e.target.value)}
              disabled={!useCustomPrompt}
              className="min-h-[150px] font-mono text-xs"
              placeholder={useCustomPrompt ? "Enter custom prompt" : isFetchingPrompt ? "Loading prompt..." : "Prompt could not be loaded"}
            />
            {!useCustomPrompt && isPromptError && (
              <p role="alert" className="text-sm text-destructive">
                Failed to load current prompt: {promptError.message}
              </p>
            )}
          </div>

          {/* Results Display */}
          {replayResult && (
            <div className="space-y-4 flex-1 overflow-hidden">
              <div className="border-t pt-4">
                <h3 className="text-sm font-medium mb-2">Results Comparison</h3>
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="w-full">
                    <TabsTrigger value="original" className="flex-1">
                      Original Output
                      <span className="ml-2 text-xs text-muted-foreground">
                        {formatTimestamp(replayResult.originalLog.timestamp)}
                      </span>
                    </TabsTrigger>
                    <TabsTrigger value="new" className="flex-1">
                      New Output
                      <span className="ml-2 text-xs text-muted-foreground">
                        {formatTimestamp(replayResult.newLog.timestamp)}
                      </span>
                    </TabsTrigger>
                    <TabsTrigger value="diff" className="flex-1">
                      Diff View
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="original" className="overflow-auto max-h-[300px]">
                    <pre className="text-xs font-mono whitespace-pre-wrap bg-muted p-4 rounded-md">
                      {prettyPrintJSON(replayResult.originalLog.parsed_output)}
                    </pre>
                  </TabsContent>

                  <TabsContent value="new" className="overflow-auto max-h-[300px]">
                    <pre className="text-xs font-mono whitespace-pre-wrap bg-muted p-4 rounded-md">
                      {prettyPrintJSON(replayResult.newLog.parsed_output)}
                    </pre>
                  </TabsContent>

                  <TabsContent value="diff" className="overflow-auto max-h-[300px]">
                    <div className="bg-muted p-4 rounded-md">
                      <p className="text-sm mb-4">
                        Showing differences between original and new outputs.
                      </p>
                      {/* Simplified diff view - in a real app you'd use a more sophisticated diff algorithm */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <h4 className="text-sm font-medium mb-2">Original</h4>
                          <pre className="text-xs font-mono whitespace-pre-wrap border-l-2 border-blue-500 pl-2">
                            {prettyPrintJSON(replayResult.originalLog.parsed_output)}
                          </pre>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium mb-2">New</h4>
                          <pre className="text-xs font-mono whitespace-pre-wrap border-l-2 border-green-500 pl-2">
                            {prettyPrintJSON(replayResult.newLog.parsed_output)}
                          </pre>
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between">
          {replayResult ? (
            <Button
              variant="outline"
              onClick={handleReset}
              className="flex items-center"
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Reset
            </Button>
          ) : (
            <Button
              variant="outline"
              disabled={useCustomPrompt || isFetchingPrompt}
              onClick={() => refetchLatestPrompt()}
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${isFetchingPrompt ? 'animate-spin' : ''}`} />
              Refresh Prompt
            </Button>
          )}

          <Button
            onClick={replayResult ? handleReset : handleReplay}
            disabled={replayMutation.isPending || (!replayResult && !canReplay)}
          >
            {replayMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : replayResult ? (
              "Try Another Replay"
            ) : (
              <>
                <Play className="mr-2 h-4 w-4" />
                Replay Log
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
